import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ArrowRightIcon, CheckBadgeIcon } from '@heroicons/react/24/outline';

function FloatingOrb({ className, delay = 0 }) {
  return (
    <motion.div
      aria-hidden
      className={`absolute rounded-full blur-3xl pointer-events-none ${className}`}
      animate={{
        scale: [1, 1.12, 1],
        opacity: [0.35, 0.55, 0.35],
        x: [0, 16, 0],
        y: [0, -12, 0],
      }}
      transition={{ duration: 12, repeat: Infinity, delay, ease: 'easeInOut' }}
    />
  );
}

function PartnerCard({ partner, index, inView }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay: 0.15 + index * 0.1 }}
      whileHover={{ y: -8 }}
      className="partners-showcase-card group relative"
    >
      <div className="partners-showcase-card-border" aria-hidden />
      <div className="partners-showcase-card-glow" aria-hidden />

      <div className="relative partners-showcase-card-inner">
        <div className="flex items-start justify-between gap-3 mb-6">
          <span className="partners-showcase-category">{partner.category}</span>
          {partner.level ? (
            <span className="partners-showcase-badge">
              <CheckBadgeIcon className="w-3.5 h-3.5" />
              {partner.level}
            </span>
          ) : (
            <span className="w-0 shrink-0" aria-hidden />
          )}
        </div>

        <img
          src={partner.logo}
          alt={partner.name}
          loading="lazy"
          decoding="async"
          draggable={false}
          className="partners-showcase-logo"
        />

        <div className="mt-6 pt-5 border-t border-slate-200/80 dark:border-gray-700/80">
          <h3 className="text-lg font-bold text-slate-900 dark:text-white tracking-tight group-hover:text-[#2563EB] transition-colors duration-300">
            {partner.name}
          </h3>
          <p className="mt-1 text-sm text-slate-500 dark:text-gray-400 leading-relaxed">
            {partner.description}
          </p>
        </div>

        <div className="partners-showcase-shine" aria-hidden />
      </div>
    </motion.article>
  );
}

export default function PartnersShowcase({ partners }) {
  const [ref, inView] = useInView({ threshold: 0.08, triggerOnce: true });

  return (
    <section
      ref={ref}
      className="partners-showcase relative py-24 md:py-32 overflow-hidden bg-gradient-to-b from-white via-slate-50/80 to-white dark:from-gray-900 dark:via-gray-900/95 dark:to-gray-900"
      aria-label="Our technology partners"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_50%_at_50%_0%,rgba(37,99,235,0.06),transparent)] pointer-events-none" />

      <FloatingOrb className="w-[28rem] h-[28rem] bg-[#2563EB]/10 -top-32 -left-24" delay={0} />
      <FloatingOrb className="w-80 h-80 bg-cyan-400/10 top-1/3 -right-20" delay={2} />
      <FloatingOrb className="w-64 h-64 bg-blue-300/10 bottom-0 left-1/3" delay={4} />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 md:mb-16 max-w-3xl mx-auto"
        >
          <span className="inline-block px-4 py-1.5 mb-4 text-xs font-semibold tracking-[0.2em] uppercase rounded-full hero-glass-tag text-[#2563EB]">
            Strategic Alliances
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 dark:text-white tracking-tight mb-4">
            Our{' '}
            <span className="bg-gradient-to-r from-[#2563EB] via-blue-500 to-cyan-500 bg-clip-text text-transparent">
              Partners
            </span>
          </h2>
          <p className="text-base md:text-lg text-slate-600 dark:text-gray-300 leading-relaxed">
            Certified alliances with global technology leaders—delivering enterprise security, networking, and infrastructure at the highest standard.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5 mb-12 md:mb-16 max-w-3xl mx-auto"
        >
          {[
            { value: '4', label: 'Tier-1 Vendors' },
            { value: '24/7', label: 'Joint Support' },
            { value: '100%', label: 'Certified' },
            { value: 'Global', label: 'Ecosystem' },
          ].map((stat, i) => (
            <div
              key={stat.label}
              className="hero-glass-card rounded-xl px-4 py-4 text-center border border-slate-200/80 dark:border-gray-700/80"
            >
              <p className="text-xl md:text-2xl font-bold bg-gradient-to-r from-[#2563EB] to-cyan-500 bg-clip-text text-transparent">
                {stat.value}
              </p>
              <p className="text-[10px] md:text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-gray-400 mt-1">
                {stat.label}
              </p>
            </div>
          ))}
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {partners.map((partner, index) => (
            <PartnerCard key={partner.name} partner={partner} index={index} inView={inView} />
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="text-center mt-14 md:mt-16"
        >
          <Link
            to="/partnerships"
            className="group inline-flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-[#2563EB] to-blue-500 text-white font-semibold rounded-xl hero-glow hover:-translate-y-0.5 transition-all duration-300"
          >
            Explore Our Alliance Network
            <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
